import {
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNotification } from '../../context/NotificationContext';
import { WaitlistEntry, WaitlistFilters, WaitlistService, WaitlistStats } from '../../services/api/waitlistService';

interface WaitlistManagementProps {
  // Add any props if needed
}

export const WaitlistManagement: React.FC<WaitlistManagementProps> = () => {
  const [stats, setStats] = useState<WaitlistStats | null>(null);
  const [entries, setEntries] = useState<WaitlistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState<WaitlistFilters>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEntry, setSelectedEntry] = useState<WaitlistEntry | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState<Partial<WaitlistEntry>>({});
  const [exporting, setExporting] = useState(false);

  const { showNotification } = useNotification();

  useEffect(() => {
    loadStats();
    loadEntries();
  }, []);

  useEffect(() => {
    loadEntries();
  }, [page, rowsPerPage, filters]);

  const loadStats = async () => {
    try {
      setStatsLoading(true);
      const statsData = await WaitlistService.getWaitlistStats();
      setStats(statsData);
    } catch (error) {
      showNotification('Failed to load waitlist statistics', 'error');
    } finally {
      setStatsLoading(false);
    }
  };

  const loadEntries = async () => {
    try {
      setLoading(true);
      const result = await WaitlistService.getWaitlistEntries({
        ...filters,
        page: page + 1,
        limit: rowsPerPage
      });
      setEntries(result.entries);
      setTotal(result.total);
    } catch (error) {
      showNotification('Failed to load waitlist entries', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setFilters(prev => ({ ...prev, search: searchTerm }));
    setPage(0);
  };

  const handleFilterChange = (key: keyof WaitlistFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEdit = (entry: WaitlistEntry) => {
    setSelectedEntry(entry);
    setEditForm({
      name: entry.name,
      email: entry.email,
      is_active: entry.is_active
    });
    setEditDialogOpen(true);
  };

  const handleView = (entry: WaitlistEntry) => {
    setSelectedEntry(entry);
    setViewDialogOpen(true);
  };

  const handleDelete = async (entry: WaitlistEntry) => {
    if (window.confirm(`Are you sure you want to delete ${entry.name}?`)) {
      try {
        await WaitlistService.deleteWaitlistEntry(entry.id);
        showNotification('Entry deleted successfully', 'success');
        loadEntries();
        loadStats();
      } catch (error) {
        showNotification('Failed to delete entry', 'error');
      }
    }
  };

  const handleResendEmail = async (entry: WaitlistEntry) => {
    try {
      await WaitlistService.resendConfirmationEmail(entry.email);
      showNotification('Confirmation email sent successfully', 'success');
    } catch (error) {
      showNotification('Failed to send confirmation email', 'error');
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedEntry) return;

    try {
      await WaitlistService.updateWaitlistEntry(selectedEntry.id, editForm);
      showNotification('Entry updated successfully', 'success');
      setEditDialogOpen(false);
      loadEntries();
      loadStats();
    } catch (error) {
      showNotification('Failed to update entry', 'error');
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const blob = await WaitlistService.exportWaitlistData(filters);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'waitlist-export.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      showNotification('Export completed successfully', 'success');
    } catch (error) {
      showNotification('Failed to export data', 'error');
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Sticky Coming Soon Header */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          backgroundColor: '#3456D5',
          py: 1.5,
          px: 2,
          mb: 3,
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          boxShadow: 2,
        }}
      >
        <InfoIcon sx={{ color: '#fff', fontSize: 20 }} />
        <Typography variant="body2" sx={{ color: '#fff', fontWeight: 500 }}>
          <strong>Coming Soon:</strong> This page is under development. All data shown is dummy and not functional.
        </Typography>
      </Box>

      <Typography variant="h4" color="text.primary" gutterBottom>
        Waitlist Management
      </Typography>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{xs: 12, sm: 6, md: 3}}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Entries
              </Typography>
              <Typography variant="h4">
                {statsLoading ? <CircularProgress size={24} /> : stats?.total_entries || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{xs: 12, sm: 6, md: 3}}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Active Entries
              </Typography>
              <Typography variant="h4">
                {statsLoading ? <CircularProgress size={24} /> : stats?.active_entries || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{xs: 12, sm: 6, md: 3}}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Top Referrers
              </Typography>
              <Typography variant="h4">
                {statsLoading ? <CircularProgress size={24} /> : stats?.top_referrers?.length || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{xs: 12, sm: 6, md: 3}}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Average Referrals
              </Typography>
              <Typography variant="h4">
                {statsLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  stats?.top_referrers && stats.top_referrers.length > 0
                    ? (stats.top_referrers.reduce((sum, ref) => sum + ref.referral_count, 0) / stats.top_referrers.length).toFixed(1)
                    : '0'
                )}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{xs: 12, sm: 6, md: 3}}>
            <TextField
              fullWidth
              label="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                endAdornment: (
                  <IconButton onClick={handleSearch}>
                    <SearchIcon />
                  </IconButton>
                ),
              }}
            />
          </Grid>
          <Grid size={{xs: 12, sm: 6, md: 2}}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.is_active ?? ''}
                onChange={(e) => handleFilterChange('is_active', e.target.value === '' ? undefined : e.target.value === 'true')}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">Active</MenuItem>
                <MenuItem value="false">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{xs: 12, sm: 6, md: 2}}>
            <FormControl fullWidth>
              <InputLabel>Has Referrals</InputLabel>
              <Select
                value={filters.has_referrals ?? ''}
                onChange={(e) => handleFilterChange('has_referrals', e.target.value === '' ? undefined : e.target.value === 'true')}
                label="Has Referrals"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">Yes</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{xs: 12, sm: 6, md: 2}}>
            <FormControl fullWidth>
              <InputLabel>Email Confirmed</InputLabel>
              <Select
                value={filters.email_confirmed ?? ''}
                onChange={(e) => handleFilterChange('email_confirmed', e.target.value === '' ? undefined : e.target.value === 'true')}
                label="Email Confirmed"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">Yes</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{xs: 12, sm: 6, md: 3}}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                onClick={() => {
                  setFilters({});
                  setSearchTerm('');
                  setPage(0);
                }}
                startIcon={<RefreshIcon />}
              >
                Clear Filters
              </Button>
              <Button
                variant="contained"
                onClick={handleExport}
                disabled={exporting}
                startIcon={exporting ? <CircularProgress size={20} /> : <DownloadIcon />}
              >
                Export
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Referral Code</TableCell>
                <TableCell>Position</TableCell>
                <TableCell>Referrals</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Email Confirmed</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : entries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    No entries found
                  </TableCell>
                </TableRow>
              ) : (
                entries.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell>{entry.name}</TableCell>
                    <TableCell>{entry.email}</TableCell>
                    <TableCell>
                      <Chip label={entry.referral_code} size="small" />
                    </TableCell>
                    <TableCell>{entry.waitlist_position}</TableCell>
                    <TableCell>
                      <Chip 
                        label={entry.referral_count} 
                        color={entry.referral_count > 0 ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={entry.is_active ? 'Active' : 'Inactive'}
                        color={entry.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={entry.email_confirmed_at ? 'Yes' : 'No'}
                        color={entry.email_confirmed_at ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatDate(entry.created_at)}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={() => handleView(entry)}>
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleEdit(entry)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Resend Email">
                          <IconButton size="small" onClick={() => handleResendEmail(entry)}>
                            <EmailIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => handleDelete(entry)} color="error">
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 20, 50, 100]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Paper>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Waitlist Entry</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Name"
              value={editForm.name || ''}
              onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Email"
              value={editForm.email || ''}
              onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
              sx={{ mb: 2 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={editForm.is_active ?? false}
                  onChange={(e) => setEditForm(prev => ({ ...prev, is_active: e.target.checked }))}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Waitlist Entry Details</DialogTitle>
        <DialogContent>
          {selectedEntry && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid size={{xs: 12, sm: 6}}>
                  <Typography variant="subtitle2" color="textSecondary">Name</Typography>
                  <Typography variant="body1">{selectedEntry.name}</Typography>
                </Grid>
                <Grid size={{xs: 12, sm: 6}}>
                  <Typography variant="subtitle2" color="textSecondary">Email</Typography>
                  <Typography variant="body1">{selectedEntry.email}</Typography>
                </Grid>
                <Grid size={{xs: 12, sm: 6}}>
                  <Typography variant="subtitle2" color="textSecondary">Referral Code</Typography>
                  <Typography variant="body1">{selectedEntry.referral_code}</Typography>
                </Grid>
                <Grid size={{xs: 12, sm: 6}}>
                  <Typography variant="subtitle2" color="textSecondary">Waitlist Position</Typography>
                  <Typography variant="body1">{selectedEntry.waitlist_position}</Typography>
                </Grid>
                <Grid size={{xs: 12, sm: 6}}>
                  <Typography variant="subtitle2" color="textSecondary">Referral Count</Typography>
                  <Typography variant="body1">{selectedEntry.referral_count}</Typography>
                </Grid>
                <Grid size={{xs: 12, sm: 6}}>
                  <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                  <Chip 
                    label={selectedEntry.is_active ? 'Active' : 'Inactive'}
                    color={selectedEntry.is_active ? 'success' : 'default'}
                  />
                </Grid>
                <Grid size={{xs: 12, sm: 6}}>
                  <Typography variant="subtitle2" color="textSecondary">Email Confirmed</Typography>
                  <Typography variant="body1">
                    {selectedEntry.email_confirmed_at ? formatDate(selectedEntry.email_confirmed_at) : 'Not confirmed'}
                  </Typography>
                </Grid>
                <Grid size={{xs: 12, sm: 6}}>
                  <Typography variant="subtitle2" color="textSecondary">Last Email Sent</Typography>
                  <Typography variant="body1">
                    {selectedEntry.last_email_sent_at ? formatDate(selectedEntry.last_email_sent_at) : 'Never'}
                  </Typography>
                </Grid>
                <Grid size={{xs: 12, sm: 6}}>
                  <Typography variant="subtitle2" color="textSecondary">Created</Typography>
                  <Typography variant="body1">{formatDate(selectedEntry.created_at)}</Typography>
                </Grid>
                <Grid size={{xs: 12, sm: 6}}>
                  <Typography variant="subtitle2" color="textSecondary">Updated</Typography>
                  <Typography variant="body1">{formatDate(selectedEntry.updated_at)}</Typography>
                </Grid>
                {selectedEntry.referrals && selectedEntry.referrals.length > 0 && (
                  <Grid size={{xs: 12}}>
                    <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                      Referrals ({selectedEntry.referrals.length})
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Created</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedEntry.referrals.map((referral) => (
                            <TableRow key={referral.id}>
                              <TableCell>{referral.name}</TableCell>
                              <TableCell>{referral.email}</TableCell>
                              <TableCell>{formatDate(referral.created_at)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}; 